import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Play, Pause } from "lucide-react";

import { useAuth } from "../AuthContext";
import { t } from "../i18n";

/**
 * MindMaster — full-screen meditation experience.
 *
 * • Uses `/mind-master/bg.png` as an unobtrusive full-screen background
 *   (object-cover, gently dimmed for legibility of controls).
 * • Auto-plays `/mind-master/meditation.wav` on entry, loops indefinitely,
 *   and cleanly releases the <audio> element on unmount or when the user
 *   navigates back.
 * • Provides one primary Play/Pause pill (bottom) and a Back button (top).
 *   Some browsers block autoplay until the user interacts — we detect this
 *   and prompt the user with the same primary pill.
 */
export default function MindMaster() {
  const { lang } = useAuth();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  // Attempt autoplay when the audio element is ready.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;
    el.loop = true;
    el.volume = 0.9;

    const tryPlay = async () => {
      try {
        await el.play();
        setPlaying(true);
      } catch (_) {
        // Autoplay blocked — user can tap the primary pill to start.
        setPlaying(false);
      }
    };
    tryPlay();

    // Cleanup: pause + detach source so the browser releases the buffer.
    return () => {
      try {
        el.pause();
        el.currentTime = 0;
        el.removeAttribute("src");
        el.load();
      } catch (_) { /* ignore */ }
    };
  }, []);

  const toggle = async () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      try {
        await el.play();
        setPlaying(true);
      } catch (_) {
        setPlaying(false);
      }
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const goBack = () => {
    // Cleanup runs via effect return; navigate away.
    navigate(-1);
  };

  return (
    <div
      data-testid="mind-master-page"
      className="fixed inset-0 z-[60] overflow-hidden bg-slate-950 text-white select-none"
    >
      {/* Background image — occupies full screen, preserves proportions */}
      <img
        src="/mind-master/bg.png"
        alt=""
        aria-hidden
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Soft gradient overlay for control legibility (top + bottom only) */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/55 via-black/20 to-transparent"
      />
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-black/60 via-black/25 to-transparent"
      />

      {/* Back button — top-left, minimalist glass pill */}
      <button
        data-testid="mind-master-back-btn"
        onClick={goBack}
        aria-label={t(lang, "mind_master_back")}
        className="absolute top-4 left-4 sm:top-5 sm:left-5 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md border border-white/25 text-white font-heading font-bold text-sm shadow-[0_6px_18px_-6px_rgba(0,0,0,0.55)] active:translate-y-0.5 transition-all"
      >
        <ArrowLeft size={16} strokeWidth={2.75} />
        <span>{t(lang, "mind_master_back")}</span>
      </button>

      {/* Title (subtle, top-center) */}
      <div className="absolute top-5 left-1/2 -translate-x-1/2 text-center">
        <h1
          data-testid="mind-master-title"
          className="font-heading text-lg sm:text-xl font-bold tracking-tight text-white/90 drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]"
        >
          {t(lang, "mind_master_title")}
        </h1>
      </div>

      {/* Slow breathing halo behind the audio button — a meditation cue */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute left-1/2 bottom-24 -translate-x-1/2 w-40 h-40 rounded-full bg-white/10 blur-2xl"
        animate={{ scale: playing ? [1, 1.25, 1] : 1, opacity: playing ? [0.35, 0.65, 0.35] : 0.25 }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Primary Play / Pause pill — bottom center */}
      <div className="absolute inset-x-0 bottom-8 sm:bottom-10 flex items-center justify-center px-6">
        <motion.button
          data-testid="mind-master-audio-toggle"
          onClick={toggle}
          whileTap={{ scale: 0.96 }}
          className={`inline-flex items-center gap-3 px-6 py-3.5 rounded-full backdrop-blur-md border border-white/25 font-heading font-bold uppercase tracking-[0.14em] text-sm shadow-[0_10px_30px_-10px_rgba(0,0,0,0.7)] transition-all
            ${playing
              ? "bg-white/20 hover:bg-white/30 text-white"
              : "bg-white text-slate-900 hover:bg-white/95"}
          `}
        >
          <AnimatePresence mode="wait" initial={false}>
            {playing ? (
              <motion.span
                key="pause"
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.7 }}
                className="inline-flex items-center gap-2"
              >
                <Pause size={18} strokeWidth={2.75} fill="currentColor" />
                <span>{t(lang, "mind_master_audio_stop")}</span>
              </motion.span>
            ) : (
              <motion.span
                key="play"
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.7 }}
                className="inline-flex items-center gap-2"
              >
                <Play size={18} strokeWidth={2.75} fill="currentColor" />
                <span>{t(lang, "mind_master_audio_start")}</span>
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </div>

      {/* Hidden audio element — controlled via ref */}
      <audio
        ref={audioRef}
        src="/mind-master/meditation.wav"
        preload="auto"
        loop
        data-testid="mind-master-audio"
      />
    </div>
  );
}
